import React, { useEffect, useState } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/admin/DashboardLayout';
import RequireAdmin from '../components/admin/RequireAdmin';
import { BarChart3, Users, MousePointerClick, TrendingUp, Eye, Calendar, Target, Award, UserCheck, Percent, Phone, Star } from 'lucide-react';

// ============ TYPES ============

interface AnalyticsData {
    totalVisitors: number;
    totalPageViews: number;
    totalFeatureClicks: number;
    totalMentorInteractions: number;
    pageViewsBySlide: { slide_index: number; slide_name: string; count: number }[];
    featureClicksByType: { feature: string; count: number }[];
    topMentors: { mentor_name: string; count: number }[];
    dailyVisitors: { date: string; count: number }[];
}

interface FounderMetrics {
    // Traffic
    uniqueSessions7d: number;
    mentorClicks7d: number;
    mentorActualContacts7d: number;
    // Mentor Demand
    totalMentors: number;
    mentorsContacted30d: number;
    totalContactClicksLifetime: number;
    topContactedMentors: { name: string; count: number }[];
    topActualContactMentors: { name: string; count: number }[];
    mentorsWithZeroContactPct: number;
    // SNBT
    totalRegisteredUsers: number;
    tryoutsStarted7d: number;
    tryoutsCompleted7d: number;
    completionRate: number;
    conversionRate: number;
}

// ============ MAIN COMPONENT ============

export default function AnalyticsPage() {
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [founderData, setFounderData] = useState<FounderMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [founderLoading, setFounderLoading] = useState(true);
    const [dateRange, setDateRange] = useState(30);
    const [activeTab, setActiveTab] = useState<'existing' | 'founder'>('founder');

    useEffect(() => {
        fetchAnalytics();
        fetchFounderMetrics();
    }, [dateRange]);

    // ============ EXISTING ANALYTICS (preserved) ============

    const fetchAnalytics = async () => {
        setLoading(true);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dateRange);
        const startDateStr = startDate.toISOString();

        try {
            const { data: pageViews, count: pvCount } = await supabase
                .from('page_views')
                .select('*', { count: 'exact' })
                .gte('viewed_at', startDateStr);

            const { data: featureClicks, count: fcCount } = await supabase
                .from('feature_clicks')
                .select('*', { count: 'exact' })
                .gte('clicked_at', startDateStr);

            const { data: mentorInteractions, count: miCount } = await supabase
                .from('mentor_interactions')
                .select('*', { count: 'exact' })
                .gte('interacted_at', startDateStr);

            const uniqueVisitors = new Set(pageViews?.map(pv => pv.user_id) || []).size;

            const slideMap: Record<string, number> = {};
            pageViews?.forEach(pv => {
                const key = pv.page || 'Unknown';
                slideMap[key] = (slideMap[key] || 0) + 1;
            });
            const pageViewsBySlide = Object.entries(slideMap)
                .map(([name, count]) => ({ slide_index: 0, slide_name: name, count }))
                .sort((a, b) => b.count - a.count);

            const featureMap: Record<string, number> = {};
            featureClicks?.forEach(fc => {
                const key = fc.feature || 'Unknown';
                featureMap[key] = (featureMap[key] || 0) + 1;
            });
            const featureClicksByType = Object.entries(featureMap)
                .map(([feature, count]) => ({ feature, count }))
                .sort((a, b) => b.count - a.count);

            const mentorMap: Record<string, number> = {};
            mentorInteractions?.forEach(mi => {
                const key = mi.mentor_id || 'Unknown';
                mentorMap[key] = (mentorMap[key] || 0) + 1;
            });
            const topMentors = Object.entries(mentorMap)
                .map(([mentor_id, count]) => ({ mentor_name: mentor_id, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 10);

            const dailyMap: Record<string, Set<string>> = {};
            pageViews?.forEach(pv => {
                const date = new Date(pv.viewed_at).toLocaleDateString('id-ID');
                if (!dailyMap[date]) dailyMap[date] = new Set();
                dailyMap[date].add(pv.user_id);
            });
            const dailyVisitors = Object.entries(dailyMap)
                .map(([date, users]) => ({ date, count: users.size }))
                .slice(-7);

            setData({
                totalVisitors: uniqueVisitors,
                totalPageViews: pvCount || 0,
                totalFeatureClicks: fcCount || 0,
                totalMentorInteractions: miCount || 0,
                pageViewsBySlide,
                featureClicksByType,
                topMentors,
                dailyVisitors,
            });
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    // ============ FOUNDER METRICS ============

    const fetchFounderMetrics = async () => {
        setFounderLoading(true);
        const now = new Date();
        const d7 = new Date(now); d7.setDate(d7.getDate() - 7);
        const d30 = new Date(now); d30.setDate(d30.getDate() - 30);
        const d7Str = d7.toISOString();
        const d30Str = d30.toISOString();

        try {
            // --- Traffic Metrics ---
            const { data: sessions7d } = await supabase
                .from('analytics_events')
                .select('session_id')
                .gte('created_at', d7Str);
            const uniqueSessions7d = new Set(sessions7d?.map(e => e.session_id).filter(Boolean) || []).size;

            const { count: mentorClicks7d } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .in('event_type', ['mentor_profile_viewed', 'mentor_contact_click'])
                .gte('created_at', d7Str);

            const { count: mentorActualContacts7d } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .eq('event_type', 'mentor_actual_contact')
                .gte('created_at', d7Str);

            // --- Mentor Demand Metrics ---
            const { count: totalMentors } = await supabase
                .from('mentors')
                .select('*', { count: 'exact', head: true });

            const { data: contacted30d } = await supabase
                .from('analytics_events')
                .select('target_id')
                .eq('event_type', 'mentor_actual_contact')
                .gte('created_at', d30Str);
            const mentorsContacted30d = new Set(contacted30d?.map(e => e.target_id).filter(Boolean) || []).size;

            const { count: totalContactLifetime } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .in('event_type', ['mentor_profile_viewed', 'mentor_contact_click']);

            // Top 5 most contacted mentors (all time from analytics_events)
            const { data: allContactEvents } = await supabase
                .from('analytics_events')
                .select('target_id, metadata, event_type')
                .in('event_type', ['mentor_profile_viewed', 'mentor_contact_click', 'mentor_actual_contact']);

            const clickedMap: Record<string, number> = {};
            const actualContactMap: Record<string, number> = {};

            allContactEvents?.forEach(e => {
                const name = (e.metadata as any)?.mentor_name || e.target_id || 'Unknown';
                if (e.event_type === 'mentor_profile_viewed' || e.event_type === 'mentor_contact_click') {
                    clickedMap[name] = (clickedMap[name] || 0) + 1;
                } else if (e.event_type === 'mentor_actual_contact') {
                    actualContactMap[name] = (actualContactMap[name] || 0) + 1;
                }
            });
            const topContactedMentors = Object.entries(clickedMap)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);

            const topActualContactMentors = Object.entries(actualContactMap)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);

            // % mentors with zero contact events
            const contactedMentorNames = new Set(Object.keys(clickedMap));
            const totalMentorCount = totalMentors || 0;
            const mentorsWithZeroContactPct = totalMentorCount > 0
                ? Math.round(((totalMentorCount - contactedMentorNames.size) / totalMentorCount) * 100)
                : 0;

            // --- SNBT Metrics ---
            const { count: totalRegisteredUsers } = await supabase
                .from('users')
                .select('*', { count: 'exact', head: true })
                .eq('role', 'user');

            const { count: tryoutsStarted7d } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .eq('event_type', 'tryout_started')
                .gte('created_at', d7Str);

            const { count: tryoutsCompleted7d } = await supabase
                .from('analytics_events')
                .select('*', { count: 'exact', head: true })
                .eq('event_type', 'tryout_completed')
                .gte('created_at', d7Str);

            const started = tryoutsStarted7d || 0;
            const completed = tryoutsCompleted7d || 0;
            const completionRate = started > 0 ? Math.round((completed / started) * 100) : 0;

            // Conversion: sessions that viewed mentor profile AND later registered
            const { data: viewerSessions } = await supabase
                .from('analytics_events')
                .select('session_id, user_id')
                .eq('event_type', 'mentor_profile_viewed');

            const viewerSessionIds = new Set(viewerSessions?.map(e => e.session_id).filter(Boolean) || []);
            const viewersWhoRegistered = new Set(
                viewerSessions?.filter(e => e.user_id).map(e => e.user_id) || []
            ).size;
            const totalViewerSessions = viewerSessionIds.size;
            const conversionRate = totalViewerSessions > 0
                ? Math.round((viewersWhoRegistered / totalViewerSessions) * 100)
                : 0;

            setFounderData({
                uniqueSessions7d,
                mentorClicks7d: mentorClicks7d || 0,
                mentorActualContacts7d: mentorActualContacts7d || 0,
                totalMentors: totalMentorCount,
                mentorsContacted30d,
                totalContactClicksLifetime: totalContactLifetime || 0,
                topContactedMentors,
                topActualContactMentors,
                mentorsWithZeroContactPct,
                totalRegisteredUsers: totalRegisteredUsers || 0,
                tryoutsStarted7d: started,
                tryoutsCompleted7d: completed,
                completionRate,
                conversionRate,
            });
        } catch (error) {
            console.error('[FounderAnalytics] Error:', error);
            // If analytics_events table doesn't exist yet, show empty state
            setFounderData(null);
        } finally {
            setFounderLoading(false);
        }
    };

    const slideNames: Record<string, string> = {
        '0': 'Beranda',
        '1': 'Smart Match',
        '2': 'Direktori',
        '3': 'Etika Chat',
        '4': 'Tentang Kami',
        '5': 'Favorit',
    };

    const PIE_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

    return (
        <RequireAdmin>
            <DashboardLayout>
                <div className="p-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <BarChart3 size={28} /> Analytics Dashboard
                        </h1>
                        <div className="flex flex-wrap gap-3 items-center w-full sm:w-auto">
                            {/* Tab Toggle */}
                            <div className="flex bg-slate-800 rounded-lg p-1">
                                <button
                                    onClick={() => setActiveTab('founder')}
                                    className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'founder' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
                                >
                                    📊 Founder
                                </button>
                                <button
                                    onClick={() => setActiveTab('existing')}
                                    className={`px-4 py-2 rounded-md text-sm font-bold transition ${activeTab === 'existing' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
                                >
                                    📈 Traffic Detail
                                </button>
                            </div>
                            <select
                                value={dateRange}
                                onChange={(e) => setDateRange(Number(e.target.value))}
                                className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                            >
                                <option value={7}>7 Hari Terakhir</option>
                                <option value={14}>14 Hari Terakhir</option>
                                <option value={30}>30 Hari Terakhir</option>
                                <option value={90}>90 Hari Terakhir</option>
                            </select>
                        </div>
                    </div>

                    {/* ============ FOUNDER TAB ============ */}
                    {activeTab === 'founder' && (
                        founderLoading ? (
                            <div className="text-center py-12 text-slate-400">Loading Founder Metrics...</div>
                        ) : founderData ? (
                            <>
                                {/* ── TRAFFIC METRICS ── */}
                                <div className="mb-8">
                                    <h2 className="text-lg font-bold text-slate-300 mb-4 flex items-center gap-2">
                                        <Eye size={20} className="text-blue-400" /> Traffic Metrics <span className="text-xs text-slate-500 ml-2">(7 hari terakhir)</span>
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <MetricCard
                                            icon={<Users size={22} />}
                                            label="Unique Sessions"
                                            value={founderData.uniqueSessions7d}
                                            color="from-blue-600 to-blue-700"
                                        />
                                        <MetricCard
                                            icon={<MousePointerClick size={22} />}
                                            label="Mentor Clicks"
                                            value={founderData.mentorClicks7d}
                                            color="from-cyan-600 to-cyan-700"
                                        />
                                        <MetricCard
                                            icon={<Phone size={22} />}
                                            label="Mentor Contacts"
                                            value={founderData.mentorActualContacts7d}
                                            color="from-emerald-600 to-emerald-700"
                                        />
                                    </div>
                                </div>

                                {/* ── MENTOR DEMAND METRICS ── */}
                                <div className="mb-8">
                                    <h2 className="text-lg font-bold text-slate-300 mb-4 flex items-center gap-2">
                                        <Target size={20} className="text-purple-400" /> Mentor Demand Metrics
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                        <MetricCard
                                            icon={<Users size={22} />}
                                            label="Total Mentors"
                                            value={founderData.totalMentors}
                                            color="from-slate-600 to-slate-700"
                                        />
                                        <MetricCard
                                            icon={<UserCheck size={22} />}
                                            label="Mentors Contacted"
                                            value={founderData.mentorsContacted30d}
                                            color="from-purple-600 to-purple-700"
                                            sublabel="30 hari"
                                        />
                                        <MetricCard
                                            icon={<MousePointerClick size={22} />}
                                            label="Total Contact Clicks"
                                            value={founderData.totalContactClicksLifetime}
                                            color="from-orange-600 to-orange-700"
                                            sublabel="lifetime"
                                        />
                                        <MetricCard
                                            icon={<Percent size={22} />}
                                            label="Zero Contact"
                                            value={`${founderData.mentorsWithZeroContactPct}%`}
                                            color="from-red-600 to-red-700"
                                            sublabel="mentors"
                                        />
                                    </div>

                                    {/* Top 5 Most Clicked */}
                                    <div className="bg-slate-800 p-6 rounded-xl mb-4">
                                        <h3 className="text-lg font-bold mb-4">⭐ Top 5 Most Clicked Mentors</h3>
                                        {founderData.topContactedMentors.length > 0 ? (
                                            <div className="space-y-3">
                                                {founderData.topContactedMentors.map((m, i) => (
                                                    <div key={i} className="flex items-center gap-4">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm ${i === 0 ? 'bg-yellow-500 text-yellow-950' : i === 1 ? 'bg-slate-300 text-slate-800' : i === 2 ? 'bg-orange-600 text-white' : 'bg-slate-700 text-slate-300'}`}>
                                                            {i + 1}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-bold text-white">{m.name}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-lg font-black text-indigo-400">{m.count}</span>
                                                            <span className="text-xs text-slate-500 ml-1">clicks</span>
                                                        </div>
                                                        <div className="w-32 hidden sm:block">
                                                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                                                                    style={{ width: `${Math.round((m.count / (founderData.topContactedMentors[0]?.count || 1)) * 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-slate-400 text-sm">Belum ada data klik mentor</p>
                                        )}
                                    </div>

                                    {/* Top 5 Most Contacted Mentors */}
                                    <div className="bg-slate-800 p-6 rounded-xl">
                                        <h3 className="text-lg font-bold mb-4">💬 Top 5 Most Contacted Mentors</h3>
                                        {founderData.topActualContactMentors.length > 0 ? (
                                            <div className="space-y-3">
                                                {founderData.topActualContactMentors.map((m, i) => (
                                                    <div key={i} className="flex items-center gap-4">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm ${i === 0 ? 'bg-yellow-500 text-yellow-950' : i === 1 ? 'bg-slate-300 text-slate-800' : i === 2 ? 'bg-orange-600 text-white' : 'bg-slate-700 text-slate-300'}`}>
                                                            {i + 1}
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="font-bold text-white">{m.name}</p>
                                                        </div>
                                                        <div className="text-right">
                                                            <span className="text-lg font-black text-emerald-400">{m.count}</span>
                                                            <span className="text-xs text-slate-500 ml-1">contacts</span>
                                                        </div>
                                                        <div className="w-32 hidden sm:block">
                                                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                                                                    style={{ width: `${Math.round((m.count / (founderData.topActualContactMentors[0]?.count || 1)) * 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-slate-400 text-sm">Belum ada data kontak WhatsApp mentor</p>
                                        )}
                                    </div>
                                </div>

                                {/* ── SNBT METRICS ── */}
                                <div className="mb-8">
                                    <h2 className="text-lg font-bold text-slate-300 mb-4 flex items-center gap-2">
                                        <Award size={20} className="text-amber-400" /> SNBT Metrics <span className="text-xs text-slate-500 ml-2">(7 hari terakhir)</span>
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                                        <MetricCard
                                            icon={<Users size={22} />}
                                            label="Registered Users"
                                            value={founderData.totalRegisteredUsers}
                                            color="from-indigo-600 to-indigo-700"
                                        />
                                        <MetricCard
                                            icon={<TrendingUp size={22} />}
                                            label="Tryouts Started"
                                            value={founderData.tryoutsStarted7d}
                                            color="from-blue-600 to-blue-700"
                                        />
                                        <MetricCard
                                            icon={<Award size={22} />}
                                            label="Tryouts Completed"
                                            value={founderData.tryoutsCompleted7d}
                                            color="from-emerald-600 to-emerald-700"
                                        />
                                        <MetricCard
                                            icon={<Percent size={22} />}
                                            label="Completion Rate"
                                            value={`${founderData.completionRate}%`}
                                            color="from-cyan-600 to-cyan-700"
                                        />
                                        <MetricCard
                                            icon={<Star size={22} />}
                                            label="Conversion Rate"
                                            value={`${founderData.conversionRate}%`}
                                            color="from-amber-500 to-orange-600"
                                            sublabel="viewer → user"
                                        />
                                    </div>

                                    {/* Conversion Explanation */}
                                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-sm text-slate-400">
                                        <p className="font-bold text-slate-300 mb-1">📐 Conversion Rate Calculation</p>
                                        <p>Distinct users who viewed a mentor profile AND later created an account ÷ Total unique sessions that viewed mentors</p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12 bg-slate-800 rounded-xl">
                                <BarChart3 size={48} className="mx-auto mb-3 text-slate-600" />
                                <p className="text-slate-400 font-bold">Founder Metrics Not Available</p>
                                <p className="text-sm text-slate-500 mt-2">Pastikan tabel <code className="bg-slate-700 px-2 py-1 rounded">analytics_events</code> sudah dibuat di Supabase.</p>
                                <p className="text-xs text-slate-600 mt-1">Jalankan file <code className="bg-slate-700 px-2 py-1 rounded">supabase/analytics_events_migration.sql</code> di SQL Editor.</p>
                            </div>
                        )
                    )}

                    {/* ============ EXISTING TRAFFIC DETAIL TAB ============ */}
                    {activeTab === 'existing' && (
                        loading ? (
                            <div className="text-center py-12">Loading analytics...</div>
                        ) : data ? (
                            <>
                                {/* Summary Cards (original) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-6 rounded-xl">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Users size={24} />
                                            <span className="text-sm opacity-80">Unique Visitors</span>
                                        </div>
                                        <div className="text-3xl font-bold">{data.totalVisitors}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-600 to-green-700 p-6 rounded-xl">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Eye size={24} />
                                            <span className="text-sm opacity-80">Page Views</span>
                                        </div>
                                        <div className="text-3xl font-bold">{data.totalPageViews}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-6 rounded-xl">
                                        <div className="flex items-center gap-3 mb-2">
                                            <MousePointerClick size={24} />
                                            <span className="text-sm opacity-80">Feature Clicks</span>
                                        </div>
                                        <div className="text-3xl font-bold">{data.totalFeatureClicks}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-orange-600 to-orange-700 p-6 rounded-xl">
                                        <div className="flex items-center gap-3 mb-2">
                                            <TrendingUp size={24} />
                                            <span className="text-sm opacity-80">Mentor Interactions</span>
                                        </div>
                                        <div className="text-3xl font-bold">{data.totalMentorInteractions}</div>
                                    </div>
                                </div>

                                {/* Charts Row */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                                    {/* Page Views by Slide */}
                                    <div className="bg-slate-800 p-6 rounded-xl">
                                        <h3 className="text-lg font-bold mb-4">📊 Page Views by Section</h3>
                                        {data.pageViewsBySlide.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={260}>
                                                <BarChart data={data.pageViewsBySlide.map(item => ({
                                                    ...item,
                                                    slide: slideNames[item.slide_name] || item.slide_name,
                                                }))} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                    <XAxis dataKey="slide" stroke="#cbd5e1" fontSize={12} />
                                                    <YAxis stroke="#cbd5e1" fontSize={12} allowDecimals={false} />
                                                    <Tooltip contentStyle={{ background: '#1e293b', border: 'none', color: '#fff' }} />
                                                    <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <p className="text-slate-400 text-sm">Belum ada data</p>
                                        )}
                                    </div>

                                    {/* Feature Clicks */}
                                    <div className="bg-slate-800 p-6 rounded-xl">
                                        <h3 className="text-lg font-bold mb-4">🖱️ Feature Clicks</h3>
                                        {data.featureClicksByType.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={260}>
                                                <BarChart data={data.featureClicksByType.slice(0, 8)} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                    <XAxis dataKey="feature" stroke="#cbd5e1" fontSize={12} />
                                                    <YAxis stroke="#cbd5e1" fontSize={12} allowDecimals={false} />
                                                    <Tooltip contentStyle={{ background: '#1e293b', border: 'none', color: '#fff' }} />
                                                    <Bar dataKey="count" fill="#22c55e" radius={[6, 6, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <p className="text-slate-400 text-sm">Belum ada data</p>
                                        )}
                                    </div>
                                </div>

                                {/* Daily Visitors */}
                                <div className="bg-slate-800 p-6 rounded-xl mb-8">
                                    <h3 className="text-lg font-bold mb-4">📅 Daily Unique Visitors (7 Hari Terakhir)</h3>
                                    {data.dailyVisitors.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={260}>
                                            <BarChart data={data.dailyVisitors} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                                <XAxis dataKey="date" stroke="#cbd5e1" fontSize={12} />
                                                <YAxis stroke="#cbd5e1" fontSize={12} allowDecimals={false} />
                                                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', color: '#fff' }} />
                                                <Bar dataKey="count" fill="#f59e42" radius={[6, 6, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <p className="text-slate-400 text-sm">Belum ada data</p>
                                    )}
                                </div>

                                {/* Top Mentors */}
                                <div className="bg-slate-800 p-6 rounded-xl">
                                    <h3 className="text-lg font-bold mb-4">⭐ Top 10 Mentor Paling Diklik</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                        {data.topMentors.map((mentor, i) => (
                                            <div key={i} className="bg-slate-700 p-4 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-yellow-400">#{i + 1}</div>
                                                <div className="text-sm mt-1 truncate">{mentor.mentor_name}</div>
                                                <div className="text-xs text-slate-400 mt-1">{mentor.count} interactions</div>
                                            </div>
                                        ))}
                                        {data.topMentors.length === 0 && (
                                            <p className="text-slate-400 text-sm col-span-5">Belum ada data</p>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12 text-slate-400">Gagal memuat data analytics</div>
                        )
                    )}
                </div>
            </DashboardLayout>
        </RequireAdmin>
    );
}

// ============ REUSABLE COMPONENTS ============

function MetricCard({ icon, label, value, color, sublabel }: {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    color: string;
    sublabel?: string;
}) {
    return (
        <div className={`bg-gradient-to-br ${color} p-5 rounded-xl`}>
            <div className="flex items-center gap-3 mb-2">
                {icon}
                <span className="text-sm opacity-80">{label}</span>
            </div>
            <div className="text-3xl font-bold">{value}</div>
            {sublabel && <p className="text-xs opacity-60 mt-1">{sublabel}</p>}
        </div>
    );
}
